import { useState } from "react";
import { Loader2, Shuffle } from "lucide-react";

export default function AnagramPanel({ text, anagrams, loading }) {
  const [showAll, setShowAll] = useState(false);

  return (
    <div className="rise rise-3 panel p-6 md:p-8" data-testid="anagram-panel">
      <div className="flex items-center gap-3 mb-1">
        <Shuffle className="gold shrink-0" size={16} />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] gold">
          // Hidden Words
        </div>
      </div>
      <h2 className="font-display text-2xl md:text-3xl tracking-tight">
        Words hiding <span className="gold italic">inside</span> "{text.trim()}"
      </h2>
      <p className="muted text-sm mt-2 leading-relaxed">
        Every dictionary phrase that can be spelt using only the letters in your input
        (accounting for repeats). Full anagrams (uses every letter) are marked ⭐.
      </p>

      {loading && (
        <div className="mt-6 flex items-center gap-3 muted text-sm">
          <Loader2 className="animate-spin" size={14} /> Searching hidden words…
        </div>
      )}

      {!loading && anagrams && anagrams.total === 0 && (
        <div className="mt-6 muted text-sm italic">
          No dictionary phrases fit inside these letters.
        </div>
      )}

      {!loading && anagrams && anagrams.total > 0 && (
        <>
          <div className="mt-5 flex items-baseline gap-4 flex-wrap">
            <div className="font-mono text-xs muted">
              <span className="gold text-lg font-semibold">{anagrams.total}</span> total
            </div>
            {anagrams.total_full_anagrams > 0 && (
              <div className="font-mono text-xs muted">
                ⭐ <span className="gold font-semibold">{anagrams.total_full_anagrams}</span> full anagram{anagrams.total_full_anagrams === 1 ? "" : "s"}
              </div>
            )}
            <div className="font-mono text-xs muted">
              {anagrams.input_letter_count} letters in
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(showAll ? anagrams.results : anagrams.results.slice(0, 24)).map((h) => (
              <a
                key={h.id}
                href={`/?phrase=${encodeURIComponent(h.id)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`anagram-${h.id}`}
                className="group flex items-center justify-between gap-3 px-4 py-3 border border-white/10 hover:border-[var(--primary)] transition-colors rounded-sm bg-black/20"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg text-white group-hover:text-[var(--primary)] transition-colors truncate">
                    {h.is_full_anagram && <span className="gold mr-1">⭐</span>}
                    {h.text}
                  </div>
                  <div className="font-mono text-[10px] muted mt-0.5">
                    {h.letters_used} / {h.letters_input} letters · Ord {h.ciphers?.english_ordinal ?? "—"}
                  </div>
                </div>
              </a>
            ))}
          </div>

          {anagrams.results.length > 24 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] muted hover:text-white"
              data-testid="anagram-toggle"
            >
              {showAll ? "− Show fewer" : `+ Show all ${anagrams.results.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
